import type { IntlShape } from 'react-intl'
import { SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import {
  formatFlexibleCancellationPolicyDate,
  getFlexibleCancellationTimelineCurrentPeriod,
  getFlexibleCancellationTimelineNextPeriod,
  getFlexibleCancellationTimelinePolicyDates,
  type FlexibleCancellationTimelineDetails,
  type FlexibleCancellationTimelinePeriod,
} from 'components/Common/CancellationPolicies/Flash/flexibleCancellationPolicyTimelineUtils'

export interface OfferCancellationCollapsedSummary {
  statusTitle: string
  statusDescription: string
  nextChangeDetail: string
  /** Whether the status title should use the highlight (green) tone. */
  isStatusHighlighted: boolean
}

interface GetOfferCancellationCollapsedSummaryParams {
  details: FlexibleCancellationTimelineDetails
  isCharter: boolean
  intl: IntlShape
  formatDate: (date: moment.Moment, options: Intl.DateTimeFormatOptions) => string
}

function getStatusCopyForPeriod(params: {
  period: FlexibleCancellationTimelinePeriod
  isCharter: boolean
  intl: IntlShape
  formatDateLabel: (date: string | moment.Moment) => string
  policyDates: ReturnType<typeof getFlexibleCancellationTimelinePolicyDates>
}): Pick<OfferCancellationCollapsedSummary, 'statusTitle' | 'statusDescription' | 'isStatusHighlighted'> {
  const { period, isCharter, intl, formatDateLabel, policyDates } = params

  if (period === 'full_refund') {
    const date = formatDateLabel(policyDates.fullRefundDate)
    return {
      isStatusHighlighted: true,
      statusTitle: intl.formatMessage({
        id: 'nDckio1aomTSXr4u0m4I',
        defaultMessage: 'Full refund until {date}',
        description: 'Collapsed offer cancellation timeline status title for the full refund window',
      }, { date }),
      statusDescription: intl.formatMessage({
        id: 'OZaIOCvzrAeK9m6oOW_l',
        defaultMessage: 'Cancel before {date}, 11:59 pm for a full refund to original payment or credit.',
        description: 'Collapsed offer cancellation timeline description for the full refund window',
      }, { date }),
    }
  }

  if (period === 'credit_or_partial') {
    const date = formatDateLabel(policyDates.partialRefundDeadline)
    if (isCharter) {
      return {
        isStatusHighlighted: false,
        statusTitle: intl.formatMessage({
          id: 'gsOcjHVIg2jQL5OEBVXH',
          defaultMessage: 'Partial refund until {date}',
          description: 'Collapsed offer cancellation timeline status title for charter partial refund',
        }, { date }),
        statusDescription: intl.formatMessage({
          id: '91Jo6vd58n_4z4-GHsCy',
          defaultMessage: 'Cancel before {date}, 11:59 pm to get a partial refund. Cancellation fees apply.',
          description: 'Collapsed offer cancellation timeline description for charter partial refund',
        }, { date }),
      }
    }

    return {
      isStatusHighlighted: false,
      statusTitle: intl.formatMessage({
        id: 'eaugaNB2CYj3ryHDJPGI',
        defaultMessage: 'Full refund to credit until {date}',
        description: 'Collapsed offer cancellation timeline status title for credit-only refund window',
      }, { date }),
      statusDescription: intl.formatMessage({
        id: 'Kwh_ILKcoHMaNC3gWcXR',
        defaultMessage: 'Cancel before {date}, 11:59 pm to receive 100% refund to credit.',
        description: 'Collapsed offer cancellation timeline description for credit-only refund window',
      }, { date }),
    }
  }

  // Match FlexibleCancellationPolicyModalWithDates: node/title uses noRefundDate (first
  // no-refund calendar day); body uses partialRefundDeadline with "after …, 11:59 pm"
  // (last moment of the prior window). Same instant, two phrasings — do not swap these.
  const noRefundFromDate = formatDateLabel(policyDates.noRefundDate)
  return {
    isStatusHighlighted: false,
    statusTitle: intl.formatMessage({
      id: 'bddgB2Yo08S24A-slzrS',
      defaultMessage: 'No refund from {date}',
      description: 'Collapsed offer cancellation timeline status title for the non-refundable window',
    }, { date: noRefundFromDate }),
    statusDescription: intl.formatMessage({
      id: 'uAp36yEj3ExiDDpUkrR6',
      defaultMessage: 'Cancellations after {date}, 11:59 pm are non-refundable.',
      description: 'Collapsed offer cancellation timeline description for the non-refundable window',
    }, { date: formatDateLabel(policyDates.partialRefundDeadline) }),
  }
}

function getNextChangeDetail(params: {
  nextPeriod: FlexibleCancellationTimelinePeriod | 'check_in'
  isCharter: boolean
  intl: IntlShape
  formatDateLabel: (date: string | moment.Moment) => string
  policyDates: ReturnType<typeof getFlexibleCancellationTimelinePolicyDates>
  checkInDate: string | moment.Moment
}): string {
  const { nextPeriod, isCharter, intl, formatDateLabel, policyDates, checkInDate } = params

  if (nextPeriod === 'credit_or_partial') {
    const date = formatDateLabel(policyDates.partialRefundDate)
    return isCharter
      ? intl.formatMessage({
          id: 'Sn4qFUARpMBJTLh_jsQs',
          defaultMessage: '{date} · partial refund',
          description: 'Collapsed offer cancellation timeline next change row for charter partial refund',
        }, { date })
      : intl.formatMessage({
          id: 'tGqm4UD6-lAzrJr_G_MZ',
          defaultMessage: '{date} · refund to credit',
          description: 'Collapsed offer cancellation timeline next change row for full refund to credit',
        }, { date })
  }

  if (nextPeriod === 'no_refund') {
    return intl.formatMessage({
      id: 'MTkDuDdu5DyY8QmdrfS5',
      defaultMessage: '{date} · no refund',
      description: 'Collapsed offer cancellation timeline next change row for non-refundable period',
    }, { date: formatDateLabel(policyDates.noRefundDate) })
  }

  return isCharter
    ? intl.formatMessage({
        id: 'y2YPGfiGHfqKhwCAjchJ',
        defaultMessage: '{date} · Departure',
        description: 'Collapsed offer cancellation timeline next change row for charter departure',
      }, { date: formatDateLabel(checkInDate) })
    : intl.formatMessage({
        id: 'o9hCcGYLSQE7xX_pr4dn',
        defaultMessage: '{date} · Check-in',
        description: 'Collapsed offer cancellation timeline next change row for hotel check-in',
      }, { date: formatDateLabel(checkInDate) })
}

function getWithoutDatesCollapsedSummary(
  details: FlexibleCancellationTimelineDetails,
  isCharter: boolean,
  intl: IntlShape,
): OfferCancellationCollapsedSummary {
  return {
    isStatusHighlighted: true,
    statusTitle: intl.formatMessage({
      id: '71yHAmy4fBFLPBln0Gjk',
      defaultMessage: 'Full refund up to {days} days after purchase',
      description: 'Collapsed offer cancellation timeline status title when no travel dates are selected',
    }, { days: SEVEN_DAYS_AFTER_PURCHASE }),
    statusDescription: intl.formatMessage({
      id: 'NN4g98SzcMw868ZaBNJv',
      defaultMessage: 'You can cancel within {days} days of purchase for a full refund to your original payment method or credit.',
      description: 'Collapsed offer cancellation timeline description when no travel dates are selected',
    }, { days: SEVEN_DAYS_AFTER_PURCHASE }),
    nextChangeDetail: isCharter
      ? intl.formatMessage({
          id: '9BsjvMRgrzDHDO9wecT8',
          defaultMessage: 'Up to {days} days prior to departure · partial refund',
          description: 'Collapsed offer cancellation next change when no dates selected for charter',
        }, { days: details.days + 1 })
      : intl.formatMessage({
          id: 'P1b6DUkbKsDW4Db75UPo',
          defaultMessage: 'Up to {days} days prior to check-in · refund to credit',
          description: 'Collapsed offer cancellation next change when no dates selected for hotel',
        }, { days: details.days + 1 }),
  }
}

/**
 * Builds collapsed-card copy for the offer-page cancellation timeline.
 * With dates, status/next-change follow the same period boundaries as the expanded timeline.
 */
export function getOfferCancellationCollapsedSummary(
  params: GetOfferCancellationCollapsedSummaryParams,
): OfferCancellationCollapsedSummary {
  const { details, isCharter, intl, formatDate } = params
  const hasDates = !!details.checkInDate && !!details.startBasedOnCheckIn

  if (!hasDates || !details.startBasedOnCheckIn || !details.checkInDate) {
    return getWithoutDatesCollapsedSummary(details, isCharter, intl)
  }

  const policyDates = getFlexibleCancellationTimelinePolicyDates({
    startBasedOnToday: details.startBasedOnToday,
    startBasedOnCheckIn: details.startBasedOnCheckIn,
  })

  const formatDateLabel = (date: string | moment.Moment) => formatFlexibleCancellationPolicyDate(
    date,
    details.today,
    formatDate,
  )

  const currentPeriod = getFlexibleCancellationTimelineCurrentPeriod({
    today: details.today,
    policyDates,
    cancellationAfterChangeOfMindPeriod: details.cancellationAfterChangeOfMindPeriod,
  })
  const nextPeriod = getFlexibleCancellationTimelineNextPeriod(
    currentPeriod,
    details.cancellationAfterChangeOfMindPeriod,
  )

  return {
    ...getStatusCopyForPeriod({
      period: currentPeriod,
      isCharter,
      intl,
      formatDateLabel,
      policyDates,
    }),
    nextChangeDetail: getNextChangeDetail({
      nextPeriod,
      isCharter,
      intl,
      formatDateLabel,
      policyDates,
      checkInDate: details.checkInDate,
    }),
  }
}
